import {
	DetailGrid,
	marketingHead,
	PageIntro,
	Section,
	SplitRow,
	Steps,
} from "@marketing/components/blocks";
import {
	BranchVisual,
	InboxVisual,
	WhatsAppVisual,
} from "@marketing/components/visuals";
import { Closing } from "@marketing/home/Closing";
import { Faq } from "@marketing/home/Faq";
import { HERO_PRODUCTS } from "@marketing/site";
import { createFileRoute } from "@tanstack/react-router";
import { CheckIcon } from "lucide-react";

const FAQ = [
	{
		q: "What is an AI shopping agent?",
		a: "An AI shopping agent understands what a shopper asks in plain language, searches the store's catalog, checks which products really match, and builds the basket. Unlike a search bar it works from meaning, budgets and recipes, not only words.",
	},
	{
		q: "What is agentic commerce?",
		a: "Agentic commerce is shopping done with an AI agent that takes actions for the shopper, such as finding products and filling a basket. Hephon Commerce keeps the final step with the shopper: nothing is ordered until they confirm.",
	},
	{
		q: "How do you stop it from inventing products?",
		a: "The assistant can only show products returned by your catalog search, and every result is checked against the shopper's request before it is shown. Prices and stock come from your data, never from the model.",
	},
	{
		q: "How do I know it works before going live?",
		a: "Each retailer gets a test suite built from its own catalog: shopper requests across every department, language and basket task. Every change runs against it before you publish.",
	},
	{
		q: "How long does it take to start?",
		a: "With a Shopify or WooCommerce store, or a product feed, a first assistant on your catalog is usually ready in days. Platforms that need a custom connector take longer; we scope it with you.",
	},
];

const P = HERO_PRODUCTS;

const SEARCH = [
	{
		title: "Plain requests",
		text: "“Snacks for six adults watching the game” becomes chips, nuts and drinks, not six basketballs.",
	},
	{
		title: "The way your shoppers write",
		text: "Saudi and Gulf Arabic and English, mixed in one message if that is how they type, and Lebanese, Arabizi and French too. It replies in the language it was asked in.",
	},
	{
		title: "Every result checked",
		text: "Each candidate is judged against the shopper's own message before it is shown, best match first.",
	},
	{
		title: "Budgets and cheapest first",
		text: "A price limit per item or one budget for a whole list, and “the cheapest rice” sorted by price.",
	},
	{
		title: "More, and similar",
		text: "“Other options” never repeats what was shown; “something similar” stays in the same aisle.",
	},
	{
		title: "Recipes, sized",
		text: "A dish and the number of people becomes ingredients, with the pack that covers each amount for the least money.",
	},
];

const RULES = [
	{
		title: "Products you never sell",
		text: "Alcohol, pork or tobacco are hidden by the server, not by a prompt, so a halal store never shows them in any channel.",
	},
	{
		title: "Your voice",
		text: "The assistant's name, tone, look, languages and the phrases your team approved for each dialect.",
	},
	{
		title: "Your team in charge",
		text: "Every conversation is in your inbox as it happens. A shopper can ask for a person, and staff can take over any chat.",
	},
	{
		title: "What shoppers ask",
		text: "Insights on what people look for, what they could not find and why conversations end.",
	},
];

const TESTS = [
	{
		title: "Every department",
		text: "It must search before it may say a product is not sold.",
	},
	{
		title: "Finding the product",
		text: "The right product, or its sibling, near the top of the results.",
	},
	{
		title: "Baskets",
		text: "The right items in the right quantities, within budgets.",
	},
	{
		title: "Language",
		text: "The reply in the shopper's language and script.",
	},
	{
		title: "Safety",
		text: "Restricted products never offered, in any wording.",
	},
	{
		title: "WhatsApp",
		text: "Replies that fit WhatsApp's buttons, lists and checkout.",
	},
];

const START = [
	{
		title: "Connect your catalog",
		text: "Shopify, WooCommerce, a product feed or your API, with each branch's prices and stock.",
	},
	{
		title: "Set your rules",
		text: "Name, tone, languages, restricted products and when to hand over to your team.",
	},
	{
		title: "Test on your products",
		text: "A test suite built from your own catalog runs before anything goes live.",
	},
	{
		title: "Go live",
		text: "In your app, on WhatsApp and on calls, one channel at a time or all at once.",
	},
];

export const Route = createFileRoute("/_marketing/how-it-works")({
	head: () =>
		marketingHead({
			title: "How an AI Shopping Agent Works | Hephon Commerce",
			description:
				"How our AI shopping agent understands shoppers, searches your catalog, checks every result and fills the basket, tested on your products first.",
			path: "/how-it-works",
			breadcrumb: ["How it works"],
			faq: FAQ,
		}),
	component: HowItWorksPage,
});

function HowItWorksPage() {
	return (
		<>
			<PageIntro
				kicker="How it works"
				title="How the AI shopping agent turns a question into an order"
				lead="Hephon Commerce connects to your catalog, follows your rules and answers shoppers in their own words. This is what happens in the second between a message and a full basket."
			>
				<TurnVisual />
			</PageIntro>

			<Section
				id="search-title"
				title="Search that understands people."
				lead="Shoppers do not type product names. They describe a need, a meal, a budget or a party, in whatever language they think in."
			>
				<DetailGrid items={SEARCH} />
			</Section>

			<Section
				id="orders"
				tone="mist"
				title="From basket to order, on your systems."
				lead="Prices and stock come from the shopper's branch. Orders go where your orders go today."
			>
				<div className="space-y-20">
					<SplitRow
						title="The shopper's branch, its prices"
						text="Each branch keeps its own prices and stock. Where prices differ by area, the assistant asks where the shopper is first, so nothing priced is offered until it knows."
						points={[
							"A location pin or a typed area picks the nearest branch",
							"Only what that branch can deliver is offered",
						]}
						visual={<BranchVisual className="mb-8" />}
					/>
					<SplitRow
						reverse
						title="The shopper confirms, every time"
						text="The assistant builds the basket and the checkout. The order is placed only when the shopper confirms with a tap, a signed checkout page or the keypad on a call. A typed or spoken yes is never enough."
						points={[
							"Orders sent to your platform through its API, or to your team as a ticket",
							"Order status pages and WhatsApp updates for the shopper",
							"Webhooks for every order and basket event",
						]}
						visual={<WhatsAppVisual />}
					/>
				</div>
			</Section>

			<Section
				id="rules-title"
				title="Your rules, enforced by the server."
				lead="What the assistant may sell, how it sounds and when a person steps in are settings you control, not hopes written into a prompt."
			>
				<div className="grid grid-cols-1 items-start gap-14 lg:grid-cols-[1.1fr_0.9fr]">
					<DetailGrid items={RULES} columns={2} />
					<div className="mk-rise rounded-[28px] bg-(--mk-mist) p-3 sm:p-5">
						<InboxVisual />
					</div>
				</div>
			</Section>

			<Section
				id="tests-title"
				tone="night"
				title="Tested on your catalog before it speaks."
				lead="Before launch, and before every change you publish, the assistant answers a suite of shopper requests generated from your own products. You see what passed."
			>
				<DetailGrid items={TESTS} tone="night" />
			</Section>

			<Section id="start-title" title="How we start." center>
				<Steps items={START} />
			</Section>

			<Faq items={FAQ} title="How it works, answered." />
			<Closing />
		</>
	);
}

/** One answer, step by step: the request, how it was read, searched and checked, and the cards. */
function TurnVisual() {
	const steps = [
		{
			label: "Understood",
			detail: "A product request · English · 6 people",
		},
		{ label: "Searched", detail: "Chips, nuts and soft drinks" },
		{ label: "Checked", detail: "Every result against the request" },
	];
	return (
		<div className="mx-auto max-w-3xl pb-10 text-start sm:pb-14">
			<p className="ms-auto w-fit max-w-[85%] rounded-[20px] rounded-br-md bg-(--mk-ink) px-4 py-2.5 text-(--mk-white) text-[15px]">
				Snacks for 6 adults watching the game tonight
			</p>
			<ol className="mt-5 grid gap-2 sm:grid-cols-3">
				{steps.map((step) => (
					<li
						key={step.label}
						className="mk-reveal flex items-start gap-2.5 rounded-2xl bg-(--mk-white) p-3.5"
					>
						<span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-(--mk-signal) text-(--mk-white)">
							<CheckIcon className="size-3" aria-hidden />
						</span>
						<span className="text-[14px] leading-snug">
							<span className="block font-medium">
								{step.label}
							</span>
							<span className="text-(--mk-muted)">
								{step.detail}
							</span>
						</span>
					</li>
				))}
			</ol>
			<p className="mt-5 max-w-[85%] rounded-[20px] rounded-bl-md bg-(--mk-white) px-4 py-2.5 text-[15px]">
				For six of you, here is a game-night spread:
			</p>
			<div className="mt-3 grid grid-cols-3 gap-2 sm:max-w-md">
				{[P.doritos, P.cashews, P.cola].map((p) => (
					<div key={p.id} className="rounded-2xl bg-(--mk-white) p-2">
						<img
							src={p.thumb}
							alt=""
							width={96}
							height={96}
							loading="lazy"
							className="aspect-square w-full object-contain"
						/>
						<p className="mt-1 truncate text-[12px]">{p.title}</p>
						<p className="font-semibold text-[13px]">${p.price}</p>
					</div>
				))}
			</div>
		</div>
	);
}
